import { CHANNELS, MASTER_VOLUME, type Channel } from "odyssey-shared";
import { localStore, readSetting, writeSetting, type SettingsStore } from "../settings.ts";

/**
 * The viewer's sound settings (ADR-085 decisions 8 and 11), kept per device in `localStorage` beside the other
 * settings: Master and each channel's volume from 0 to 100 and its mute, whether sound stops while the window is
 * in the background, and whether a whisper's alert sounds there all the same (the amendment of 2026-10-13). Every read falls back to the default where storage is missing, refuses or holds anything else.
 */

/**
 * One volume: its level from 0 to 100 and whether it is muted, which keeps the level.
 */
export interface VolumeLevel {
  readonly volume: number;
  readonly muted: boolean;
}

/**
 * Master's id among the levels.
 */
export const MASTER = "master";

/**
 * What a level is set for: Master or a channel.
 */
export type LevelId = typeof MASTER | Channel;

/**
 * Every level, the background choice, and whether a whisper's alert passes it.
 */
export interface AudioSettings {
  readonly levels: Readonly<Record<LevelId, VolumeLevel>>;
  readonly backgroundMute: boolean;
  readonly whisperBackground: boolean;
}

const PREFIX = "odyssey.audio.";
const BACKGROUND_KEY = `${PREFIX}background`;
const WHISPERS_KEY = `${PREFIX}whispers`;

/**
 * The default level of Master or a channel.
 *
 * @param id The level's id.
 * @returns Its default, unmuted.
 */
export function defaultLevel(id: LevelId): VolumeLevel {
  if (id === MASTER) {
    return { volume: MASTER_VOLUME, muted: false };
  }
  return { volume: CHANNELS.find((channel) => channel.id === id)?.volume ?? MASTER_VOLUME, muted: false };
}

/**
 * Reads a stored volume.
 *
 * @param raw The stored text, or null.
 * @param fallback The volume for anything that is not a whole number from 0 to 100.
 * @returns The volume.
 */
export function parseVolume(raw: string | null, fallback: number): number {
  if (raw === null || !/^\d{1,3}$/.test(raw)) {
    return fallback;
  }
  const volume = Number(raw);
  return volume <= 100 ? volume : fallback;
}

/**
 * Every level's id, Master first, then the channels in Settings' order.
 *
 * @returns The ids.
 */
export function levelIds(): LevelId[] {
  return [MASTER, ...CHANNELS.map((channel) => channel.id)];
}

/**
 * Reads the viewer's sound settings.
 *
 * @param store Where they are kept; this browser's `localStorage` by default.
 * @returns The settings, each its default where nothing valid is stored.
 */
export function readAudioSettings(store: SettingsStore | null = localStore()): AudioSettings {
  const levels = {} as Record<LevelId, VolumeLevel>;
  for (const id of levelIds()) {
    const fallback = defaultLevel(id);
    levels[id] = { volume: readSetting(`${PREFIX}${id}`, (raw) => parseVolume(raw, fallback.volume), store), muted: readSetting(`${PREFIX}${id}.muted`, (raw) => raw === "on", store) };
  }
  return { levels, backgroundMute: readSetting(BACKGROUND_KEY, (raw) => raw !== "play", store), whisperBackground: readSetting(WHISPERS_KEY, (raw) => raw !== "muted", store) };
}

/**
 * Keeps one level.
 *
 * @param id Master or a channel.
 * @param level The level.
 * @param store Where to keep it; this browser's `localStorage` by default.
 * @returns True when the store took both values.
 */
export function writeLevel(id: LevelId, level: VolumeLevel, store: SettingsStore | null = localStore()): boolean {
  const volume = writeSetting(`${PREFIX}${id}`, String(Math.round(Math.min(100, Math.max(0, level.volume)))), store);
  return writeSetting(`${PREFIX}${id}.muted`, level.muted ? "on" : "off", store) && volume;
}

/**
 * Keeps whether sound stops while the window is in the background.
 *
 * @param mute True to stop it, false to keep playing.
 * @param store Where to keep it; this browser's `localStorage` by default.
 * @returns True when the store took it.
 */
export function writeBackgroundMute(mute: boolean, store: SettingsStore | null = localStore()): boolean {
  return writeSetting(BACKGROUND_KEY, mute ? "mute" : "play", store);
}

/**
 * Keeps whether a whisper's alert sounds while the window is in the background and muted.
 *
 * @param through True to let it sound, false to keep it muted with everything else.
 * @param store Where to keep it; this browser's `localStorage` by default.
 * @returns True when the store took it.
 */
export function writeWhisperBackground(through: boolean, store: SettingsStore | null = localStore()): boolean {
  return writeSetting(WHISPERS_KEY, through ? "through" : "muted", store);
}
